import React, { useCallback, useMemo } from "react";
import { Table } from "antd";
import { IRuleCondition } from "../../../../../lib/types/IRuleCondition";
import { useAppSelector } from "../../../../..";
import { TableColumn } from "../../../../../lib/interfaces";
import { checkIsLoading } from "../../../../../lib/utils";

type TableDataRecord = Pick<IRuleCondition, "includeList" | "documentTypeList" | "direction">;

interface ComponentProps {
  ruleConditions: IRuleCondition[];
  ruleConditionColumnWidths: Record<keyof TableDataRecord, string>;
  rowClassNameString?: string;
}

export const RuleConditionView = (props: ComponentProps) => {
  const { ruleConditions, ruleConditionColumnWidths, rowClassNameString } = props;
  const {
    serviceUsageDocumentType,
    serviceUsageDirection,
    retailNetworks,
    serviceUsageDocumentTypeLoading,
    serviceUsageDirectionLoading,
    retailNetworksLoading,
  } = useAppSelector((state) => state.rtkDictionaries);

  const getIncludeListData = useCallback(
    (
      includeList: IRuleCondition["includeList"]
    ): {
      displayName: string;
      nameTitle: string;
    } => {
      const { retailNetworksDic, retailNetworksUniqueIdDic } = retailNetworks;
      const selectedNetworkNames: string[] = [];
      const selectedNetworkValues: number[] = [];

      if (includeList) {
        includeList.forEach((itemId) => {
          const selectedNetworkName =
            retailNetworksUniqueIdDic.find(({ objectId }) => objectId === itemId)?.stringValue ||
            "!Сеть неизвестна!";

          if (!selectedNetworkNames.includes(selectedNetworkName)) {
            selectedNetworkNames.push(selectedNetworkName);
          }

          const selectedNetworkValue = retailNetworksDic.find(
            ({ stringValue }) => stringValue === selectedNetworkName
          )?.objectId;
          if (selectedNetworkValue && !selectedNetworkValues.includes(selectedNetworkValue)) {
            selectedNetworkValues.push(selectedNetworkValue);
          }
        });
      }

      let displayName = "";
      let nameTitle = "";

      if (selectedNetworkNames.length) {
        if (selectedNetworkNames.length > 1) {
          displayName = `${selectedNetworkNames[0]} +${selectedNetworkNames.length - 1}...`;
          nameTitle = selectedNetworkNames.join("\n");
        } else {
          [displayName] = selectedNetworkNames;
        }
      }

      return { displayName, nameTitle };
    },
    [retailNetworks]
  );

  const getDocumentTypeListData = useCallback(
    (
      documentTypeList: IRuleCondition["documentTypeList"]
    ): { displayName: string; nameTitle: string } => {
      const documentNamesList: string[] = [];

      if (documentTypeList) {
        documentTypeList.forEach((docId) => {
          const docTypeAlias = serviceUsageDocumentType.find(({ id }) => id === docId)?.alias;
          if (docTypeAlias && !documentNamesList.includes(docTypeAlias)) {
            documentNamesList.push(docTypeAlias);
          }
        });
      }

      let displayName = "";
      let nameTitle = "";

      if (documentNamesList.length) {
        if (documentNamesList.length > 1) {
          displayName = `${documentNamesList[0]} +${documentNamesList.length - 1}...`;
          nameTitle = documentNamesList.join("\n");
        } else {
          [displayName] = documentNamesList;
        }
      }

      return { displayName, nameTitle };
    },
    [serviceUsageDocumentType]
  );

  const columns = useMemo<TableColumn<TableDataRecord>[]>(
    () => [
      {
        title: "Контрагент",
        key: "includeList",
        dataIndex: "includeList",
        width: ruleConditionColumnWidths.includeList,
        render: (v, { includeList }) => {
          const { displayName, nameTitle } = getIncludeListData(includeList);
          return <span title={nameTitle}>{displayName}</span>;
        },
      },
      {
        title: "Тип",
        key: "documentTypeList",
        dataIndex: "documentTypeList",
        width: ruleConditionColumnWidths.documentTypeList,
        render: (v, { documentTypeList }) => {
          const { displayName, nameTitle } = getDocumentTypeListData(documentTypeList);
          return <span title={nameTitle}>{displayName}</span>;
        },
      },
      {
        title: "Направление",
        key: "direction",
        dataIndex: "direction",
        width: ruleConditionColumnWidths.direction,
        render: (direction) => {
          const displayName =
            serviceUsageDirection.find(({ alias }) => {
              if (direction) {
                return alias === direction;
              }
              return alias === "all";
            })?.name || "";

          return displayName;
        },
      },
    ],
    [ruleConditionColumnWidths, getIncludeListData, getDocumentTypeListData, serviceUsageDirection]
  );

  const tableData = useMemo<TableDataRecord[]>(() => {
    const rules: TableDataRecord[] = ruleConditions.map(
      ({ includeList, documentTypeList, direction }, key) => {
        return { includeList, documentTypeList, direction, key };
      }
    );
    return rules;
  }, [ruleConditions]);

  // console.log(ruleConditionTableData)

  return (
    <>
      {ruleConditions.length > 0 && (
        <Table
          showHeader={false}
          size="small"
          className="table_no-hover"
          rowClassName={rowClassNameString}
          dataSource={tableData}
          columns={columns}
          pagination={false}
          loading={checkIsLoading(
            serviceUsageDocumentTypeLoading,
            serviceUsageDirectionLoading,
            retailNetworksLoading
          )}
        />
      )}
    </>
  );
};
